import { useEffect } from "react"

import { BandSpectrumTable } from "@/components/terminal/band-spectrum-table"
import {
  CloseMark,
  terminalDialogCloseClassName,
} from "@/components/terminal/dialog-close"
import { terminalDialogClassName } from "@/components/terminal/surfaces"
import {
  ASSESSMENT_INTRO,
  type AssessmentBand,
} from "@/constants/terminal/assessment-band"
import { cn } from "@/lib/utils"

// The assessment legend: clicking the File-header band opens this reference
// panel, which states what an assessment is, then tables the band spectrum,
// worst first with UNCLEAR between WATCH and STABLE: each band's chip, score
// range and definition. The File's band is marked and the rest dimmed; opened
// from the Assessment key, with no band of its own, every band reads alike. It
// wears the one dialog skin (the close outside the corner) one measure wider
// than a confirmation, with no title and no box inside the box: the
// definition leads (Dhanur, 25 Oct 2026). Rendered inline inside the
// themed shell root, so it inherits the terminal tokens.
export const AssessmentLegend = ({
  currentBand,
  onClose,
}: {
  currentBand?: AssessmentBand
  onClose: () => void
}) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose()
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [onClose])

  return (
    <div
      onClick={onClose}
      className="bg-surface-scrim fixed inset-0 z-[1000] flex items-center justify-center p-4"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Assessment spectrum"
        onClick={(event) => event.stopPropagation()}
        className={cn(
          terminalDialogClassName,
          "relative flex w-180 max-w-[90vw] flex-col"
        )}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className={terminalDialogCloseClassName}
        >
          <CloseMark />
        </button>
        {/* No title: the definition leads (Dhanur, 25 Oct 2026). */}
        <p className="type-brief max-w-2xl">{ASSESSMENT_INTRO}</p>
        {/* The scroll lives inside, so the close outside the corner is
            never clipped. */}
        <div className="max-h-[70vh] overflow-y-auto">
          <BandSpectrumTable currentBand={currentBand} />
        </div>
      </div>
    </div>
  )
}
